import React from 'react';
import { Grid, Paper, Box, Stack, Typography } from '@mui/material';
import { MapSyncProvider } from './maps/map_syncro.js';
import MapLeft from './maps/map_left.js';
import MapRight from './maps/map_right.js';
import LoginMapRight from '../login/login_mapRight.js';

/**
 * Container component for the performance maps
 * @param {Object} props - Component props
 * @returns {JSX.Element} MapContainer component
 */
const MapContainer = ({
  prettyModelName,
  issueTime,
  cqlSevere,
  cqlFecha,
  mapLeftLayer,
  weekNumber,
  selectedTime,
  isAuthenticatedRight,
  mapRightLayer,
  selectedModel,
  onLogin
}) => {
  return (
    <div className="map-container">
      <Grid container spacing={2}>
        {/* Left Map */}
        <Grid item xs={12} md={6} sx={{ height: '100%', width: '100%' }}>
          <Paper elevation={2} sx={{ padding: 0.25 }}>
            <Box>
              <Box sx={{ px: 1, pt: 0.75, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    display: 'inline-flex',
                    background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 52%, #4ade80 100%)',
                    borderRadius: '20px',
                    px: 2.2,
                    py: 0.6,
                    boxShadow: '0 3px 12px rgba(34,197,94,0.3), inset 0 1px 0 rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    backdropFilter: 'blur(4px)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 5px 16px rgba(34,197,94,0.45)' },
                  }}
                >
                  <Typography sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 600, fontFamily: '"Open Sans", sans-serif', letterSpacing: '0.03em', lineHeight: 1, whiteSpace: 'nowrap', textTransform: 'none' }}>
                    {prettyModelName}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.4 }}>
                  Forecast &middot; Issue:&nbsp;
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{issueTime} UTC</Box>
                </Typography>
              </Box>
              <MapSyncProvider>
                <MapLeft 
                  cqlSevere={cqlSevere} 
                  cqlFecha={cqlFecha} 
                  mapLeftLayer={mapLeftLayer} 
                  weekNumber={weekNumber} 
                  issueTime={issueTime} 
                  selectedTime={selectedTime}
                />
              </MapSyncProvider>
            </Box>
          </Paper>
        </Grid>

        {/* Right Map */}
        <Grid item xs={12} md={6} sx={{ height: '100%', width: '100%' }}>
          <Paper elevation={2} sx={{ padding: 0.25, position: 'relative', zIndex: 0 }}>
            <Box sx={{ px: 1, pt: 0.75, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  display: 'inline-flex',
                  background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 52%, #60a5fa 100%)',
                  borderRadius: '20px',
                  px: 2.2,
                  py: 0.6,
                  boxShadow: '0 3px 12px rgba(59,130,246,0.3), inset 0 1px 0 rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  backdropFilter: 'blur(4px)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 5px 16px rgba(59,130,246,0.45)' },
                }}
              >
                <Typography sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 600, fontFamily: '"Open Sans", sans-serif', letterSpacing: '0.03em', lineHeight: 1, whiteSpace: 'nowrap', textTransform: 'none' }}>
                  Ground Truth
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.4 }}>
                Valid:&nbsp;
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{cqlFecha} UTC</Box>
              </Typography>
            </Box>
            
            {isAuthenticatedRight ? (
              // Authenticated: show interactive map
              <MapSyncProvider>
                <MapRight 
                  cqlSevere={cqlSevere} 
                  cqlFecha={cqlFecha} 
                  mapRightLayer={mapRightLayer} 
                  weekNumber={weekNumber} 
                  issueTime={issueTime} 
                  selectedModel={selectedModel} 
                  isInteractive={true}
                  isAuthenticatedRight={isAuthenticatedRight}
                />
              </MapSyncProvider>
            ) : (
              // Not authenticated: show static image
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '45vh',
                  overflow: 'hidden',
                }}
              >
                <img 
                  src="/images/Fondo_mapRight.png" 
                  alt="Static ground truth map" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              </Box>
            )}
            
            {/* Login overlay when not authenticated */}
            {!isAuthenticatedRight && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.72)',
                  backdropFilter: 'blur(5px)',
                  zIndex: 10,
                }}
              >
                <LoginMapRight onLogin={onLogin} />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default MapContainer;
